'use client';

import {useCommonData} from '@udemy-v2/common-data';
import {useFeatures} from '@udemy-v2/react-experimentation';
import {
    EnhancedCategoriesComponent,
    type CategoryUniqueLearnerStats,
} from '@udemy-v2/react-globalization-components';

import {POPULAR_CATEGORIES_MODULE} from '@/app/lib/experimentation';

import {CategoriesSkeleton} from './categories-skeleton';
import {LazySection} from '../lazy-section/lazy-section';
import styles from './categories.module.scss';

export interface CategoriesProps {
    categoryUniqueLearnerStats?: CategoryUniqueLearnerStats | null;
}

export const Categories = ({categoryUniqueLearnerStats = null}: CategoriesProps) => {
    const {featureVariantAssignments, isLoading} = useFeatures([POPULAR_CATEGORIES_MODULE]);

    // EnhancedCategoriesComponent falls back to hardcoded learner counts when stats are
    // null, so rendering before the flag resolves would visibly flip the numbers for
    // treatment users once assignments arrive.
    if (isLoading) return <CategoriesSkeleton />;

    const showDynamicPopularCategoriesModule =
        featureVariantAssignments.get(POPULAR_CATEGORIES_MODULE)?.configuration
            ?.show_dynamic_popular_categories_module ?? false;
    const dynamicCategoryUniqueLearnerStats = showDynamicPopularCategoriesModule
        ? categoryUniqueLearnerStats
        : null;

    return (
        <div className={styles.categories} data-purpose="categories">
            <EnhancedCategoriesComponent
                priceCountryId="JP"
                categoryUniqueLearnerStats={dynamicCategoryUniqueLearnerStats}
            />
        </div>
    );
};

export interface CategoriesSectionProps {
    categoryUniqueLearnerStats?: CategoryUniqueLearnerStats | null;
}

export const CategoriesSection = ({
    categoryUniqueLearnerStats = null,
}: CategoriesSectionProps) => {
    const {visitor} = useCommonData();

    if (visitor.countryCode !== 'JP') return null;

    return (
        <LazySection placeholder={<CategoriesSkeleton />}>
            <Categories categoryUniqueLearnerStats={categoryUniqueLearnerStats} />
        </LazySection>
    );
};
